import type { ViewDetailsResponse } from '@data-access/connect/tc-tcps';
import {
  FileFolderItem,
  MissionDetailsSelection,
  RunDetailsSelection,
  SidePanelTabName,
  getVisibleModelIds,
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { useAuth } from '@trimble/trimble-id-solidjs';
import {
  MenuPosition,
  VerticalTab,
  VerticalTabPanel,
  ViewsTab,
} from '@trimble/ui-components/solidjs';
import { MeasurementsListPanel } from '@trimble/measurements-list';
import {
  FreeViewCapability,
  PointCloudStylingCapability,
} from '@trimble/web3d-capabilities';
import { FitToViewMeasurementCapabilityCoordinator } from '@trimble/web3d';
import { LoadState } from '@trimble/data-loading';
import {
  MeasurementsCapabilityProvider,
  PointCloudStylingCapabilityProvider,
  ViewsCapabilityProvider,
} from '@trimble/web3d-providers';
import { createEffect, createMemo, createSignal, JSX, Show } from 'solid-js';
import { ProjectDataTab } from './ProjectDataTab';
import { StylesTab } from './StylesTab';
import { IssueManagementTab } from './IssueManagementTab';
import { createLayersSidePanelTab } from './layersSidePanelTab';
import { useWorkspacesSidePanelTab } from './workspacesSidePanelTab';
import {
  type TcRegion,
  useConnectProject,
} from '@trimble/connect-project-providers';
import { useLoadView } from '../views/use-load-view';

export const SidePanel = (props: {
  selectedTab?: SidePanelTabName;
  onTabChange?(tab: SidePanelTabName, isOpen: boolean): void;
  onViewDetailsSelect?(view: ViewDetailsResponse): void;
  onCreateViewClick?(): void;
  onModelDetailsSelect?(model: FileFolderItem): void;
  onRunDetailsSelect?(run: RunDetailsSelection): void;
  onMissionDetailsSelect?(mission: MissionDetailsSelection): void;
  issueManagementCreateTopicRequest?: {
    readonly type: 'create-topic';
    readonly id: number;
  };
  onIssueManagementCreateTopicRequestConsumed?(): void;
}): JSX.Element => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const auth = useAuth();
  const { selectedRegion, selectedProject } = useConnectProject();
  const { tcTcpsApi, scanDataApi } = useApiServices();
  const { dataState } = useDataContext();
  const workspacesSidePanelTab = useWorkspacesSidePanelTab();
  const loadView = useLoadView();

  const viewsProvider = capabilityRegistry.get(ViewsCapabilityProvider);
  const measurementsProvider = capabilityRegistry.get(
    MeasurementsCapabilityProvider
  );

  const fitToViewMeasurementCapabilityCoordinator = capabilityRegistry.get(
    FitToViewMeasurementCapabilityCoordinator
  );

  const pointCloudStylingProvider = capabilityRegistry.get(
    PointCloudStylingCapabilityProvider
  );

  const pointCloudStylingCapability = capabilityRegistry.get(
    PointCloudStylingCapability
  );

  const pointCloudStyling = createMemo(() => {
    const provider = pointCloudStylingProvider();
    const capability = pointCloudStylingCapability();
    return provider && capability ? { provider, capability } : undefined;
  });

  const withSelectedRegion = (
    run: (region: TcRegion) => Promise<void>
  ): Promise<void> => {
    const region = selectedRegion();
    if (!region) return Promise.reject(new Error('No region selected'));
    return run(region);
  };

  const [selectedTab, setSelectedTab] = createSignal<SidePanelTabName>();

  const toggleSelectedMeasurement = (measurementId: string): void => {
    const provider = measurementsProvider();
    if (!provider) return;

    const setMeasurementId =
      measurementId === provider.selectedItemId() ? null : measurementId;

    void provider.setSelectedItemId(setMeasurementId);
    if (setMeasurementId === null) return;

    const coordinator = fitToViewMeasurementCapabilityCoordinator();
    if (!coordinator) return;

    coordinator.fitMeasurementToView(setMeasurementId, [FreeViewCapability]);
  };

  const handleTabChange = (tab: SidePanelTabName, isOpen: boolean): void => {
    const setTab = isOpen ? tab : undefined;
    setSelectedTab(setTab);
    props.onTabChange?.(tab, isOpen);
  };

  // wait for full model list load before views can trigger model loading.
  const viewsPrerequisitesMet = createMemo(() => {
    return (
      dataState.trajectory.state === LoadState.LoadComplete &&
      dataState.pointCloud.state === LoadState.LoadComplete &&
      dataState.tcFileFolder.state === LoadState.LoadComplete &&
      !!selectedRegion() &&
      !!selectedProject()
    );
  });

  const viewsHasErrors = createMemo(() => {
    return (
      dataState.trajectory.state === LoadState.Error ||
      dataState.pointCloud.state === LoadState.Error ||
      dataState.tcFileFolder.state === LoadState.Error
    );
  });

  const viewsIsLoading = createMemo(() => {
    if (!selectedProject()) return false;
    if (viewsHasErrors()) return false;
    if (viewsPrerequisitesMet()) return false;
    return true;
  });

  const projectDataTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.ProjectData,
    tooltip: {
      text: t('mobileMapping.projectData.projectData'),
      position: 'right',
    },
    icon: 'master_data',
    iconClass: 'modus-icons',
    content: () => (
      <ProjectDataTab
        onModelDetailsSelect={props.onModelDetailsSelect}
        onRunDetailsSelect={props.onRunDetailsSelect}
        onMissionDetailsSelect={props.onMissionDetailsSelect}
      />
    ),
  };

  const viewsTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.Views,
    tooltip: {
      text: t('mobileMapping.views.title'),
      position: 'right',
    },
    icon: 'tc-icon-views',
    content: () => (
      <Show when={viewsProvider()}>
        <ViewsTab
          tcTcpsApi={tcTcpsApi}
          region={selectedRegion}
          projectId={() => selectedProject()?.id}
          defaultSortBy="createdOn"
          defaultSortDirection="desc"
          userEmail={() => auth().user?.email}
          getVisibleModelIds={() => getVisibleModelIds(dataState) ?? []}
          arePrerequisitesMet={viewsPrerequisitesMet}
          hasErrors={viewsHasErrors}
          isLoading={viewsIsLoading}
          deletePointCloudAssociationsForView={(projectId, viewId) =>
            withSelectedRegion((region) =>
              scanDataApi.deletePointCloudAssociationsForView(
                region,
                projectId,
                viewId
              )
            )
          }
          deleteReservedViewData={(viewId) =>
            withSelectedRegion((region) =>
              tcTcpsApi.deleteReservedViewData(region, viewId, 'numark')
            )
          }
          onCardClick={loadView}
          onViewDetailsSelect={props.onViewDetailsSelect}
          onCreateViewClick={props.onCreateViewClick}
        />
      </Show>
    ),
  };

  const stylesTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.Styles,
    tooltip: {
      text: t('mobileMapping.styles.header'),
      position: 'right',
    },
    icon: 'tc-icon-color',
    content: () => (
      <Show when={pointCloudStyling()} keyed>
        {(data) => (
          <StylesTab
            pointCloudStylingProvider={data.provider}
            pointCloudStylingCapability={data.capability}
            isVisible={() => selectedTab() === SidePanelTabName.Styles}
          />
        )}
      </Show>
    ),
  };

  const measurementsTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.Measurements,
    tooltip: {
      text: t('measurements.measurements'),
      position: 'right',
    },
    icon: 'tc-icon-measure',
    content: () => (
      <Show when={measurementsProvider()} keyed>
        {(provider) => (
          <MeasurementsListPanel
            measurementsProvider={provider}
            onMeasurementClick={toggleSelectedMeasurement}
          />
        )}
      </Show>
    ),
  };

  const issueManagementTab: VerticalTab<SidePanelTabName> = {
    name: SidePanelTabName.IssueManagement,
    tooltip: {
      text: t('mobileMapping.issueManagement.title'),
      position: 'right',
    },
    icon: 'tc-icon-bcf',
    preload: true,
    content: () => (
      <Show when={viewsProvider()} keyed>
        {(provider) => (
          <IssueManagementTab
            viewsProvider={provider}
            createTopicRequest={props.issueManagementCreateTopicRequest}
            onCreateTopicRequestConsumed={
              props.onIssueManagementCreateTopicRequestConsumed
            }
          />
        )}
      </Show>
    ),
  };

  const tabs = createMemo<VerticalTab<SidePanelTabName>[]>(() => [
    projectDataTab,
    ...workspacesSidePanelTab(),
    createLayersSidePanelTab(),
    viewsTab,
    stylesTab,
    measurementsTab,
    issueManagementTab,
  ]);

  createEffect(() => {
    const tab = props.selectedTab;
    if (
      tab === SidePanelTabName.Workspaces &&
      workspacesSidePanelTab().length === 0
    ) {
      handleTabChange(SidePanelTabName.Workspaces, false);
      return;
    }
    setSelectedTab(tab);
  });

  return (
    <div class="h-full w-full">
      <VerticalTabPanel
        tabs={tabs()}
        menuPosition={MenuPosition.LEFT}
        selectedTab={selectedTab()}
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        enableDarkMode={true}
        onTabToggle={handleTabChange}
      />
    </div>
  );
};
